import { useMutation, useQueryClient } from "@tanstack/react-query";
import { rejectAtsApplicationsBulk } from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";
import type { PipelineActionBody } from "@/features/jobs/store/pipeline/selection";

interface BulkVars {
  jobId: string;
  selection: PipelineActionBody;
  rejectionReasonId: string;
  note?: string | null;
}

/** Bulk ATS reject: one reason/note applied to all selected candidates (spec B3). */
export function useRejectInAtsBulk(connectionId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ jobId, selection, rejectionReasonId, note }: BulkVars) =>
      rejectAtsApplicationsBulk(connectionId, {
        job_id: jobId,
        ...selection,
        rejection_reason_id: rejectionReasonId,
        note: note ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });

  return {
    rejectBulk: async (vars: BulkVars) => {
      try {
        const res = await mutation.mutateAsync(vars);
        const failed = (res.results ?? []).filter((r) => !r.ok);
        if (failed.length > 0) {
          const resolvedCount = res.resolved_count ?? res.results.length;
          showToast.warning(
            `${failed.length} of ${resolvedCount} could not be rejected in the ATS.`,
          );
        }
        return res;
      } catch (err) {
        const description =
          err instanceof Error ? err.message : "The ATS rejected this action.";
        showToast.error("Couldn't reject in ATS", { description });
        throw err;
      }
    },
    isPending: mutation.isPending,
  };
}
